/**
 * Canonical content identity for cloud versions (migration 006).
 *
 * Platform-neutral by construction: no browser globals, no node:* imports, no
 * builder-core imports. Must run identically under jsdom, Node (≥20, for
 * globalThis.crypto), and workerd — the client computes the hash at save time
 * and tests recompute it from re-read bundles; they only agree if both run
 * this exact code.
 *
 * WHY THIS EXISTS — neither existing hash can serve content identity:
 *  - `project_versions.hash` digests the raw ZIP bytes, and JSZip stamps a
 *    wall-clock mtime into every entry, so byte-identical content still
 *    hashes differently (documented at core/save-load.js "skipIfUnchanged").
 *  - `stateHashOf` (collab-protocol.js) is a 64-bit FNV over the LIVE state
 *    minus only LOCAL_ONLY_PATHS. It is a collab divergence hint: not
 *    collision-resistant, computed over un-prepared state, and its ignore set
 *    is narrower than the diff engine's. Do not conflate the two.
 *
 * This hash is SHA-256 (collision-resistant — it anchors publish baselines
 * and "no changes between these versions" claims) over `canonicalStringify`
 * of the PREPARED portable state (the exact object save-load writes into the
 * bundle's state.json) minus VERSION_HASH_IGNORED_PATHS.
 *
 * THE INVARIANT THAT KEEPS THIS MEANINGFUL: hash equality must coincide with
 * `diffVersionStates(a, b).identical` (shared/version-diff.js). Both modules
 * therefore share this one ignore list — never grow one without the other;
 * version-diff-tests.js asserts the parity on a fixture battery.
 */

import { canonicalStringify } from './collab-protocol.js';
import { acceptKeysForIdentity } from './audit-accept-schema.js';

/**
 * Top-level state fields excluded from content identity, in four groups:
 *  - view/bookkeeping (LOCAL_ONLY_PATHS): activePage, updatedAt, cloudVersion
 *  - engine metadata: schemaVersion (migrations bump it without content change)
 *  - identity, not content: projectId, ownerSub, createdAt (a duplicated
 *    project is the same CONTENT under a different identity)
 *  - provenance of the original composition, never edited by users:
 *    sourceTemplateDomain, sourceClientProfile, compositionContext, brandKit
 *
 * `brandKit` (LN-4a) belongs to that last group: it records WHICH LogoNuri kit
 * a project took and at what revision, not what the project contains. The
 * content it produced — theme tokens, section markup, pwaInstall.appIcon,
 * page SEO — is hashed normally. Two consequences, both deliberate:
 *   · `appliedAt` is a wall-clock stamp, so hashing the record would make two
 *     byte-identical projects diff as edited.
 *   · Re-importing an unchanged kit rewrites only provenance, and correctly
 *     produces no new version.
 * Asset GC is unaffected: it scans JSON.stringify(state) for pb-asset:// and
 * does not consult this list, so brandKit.assets still pins unplaced variants.
 */
export const VERSION_HASH_IGNORED_PATHS = Object.freeze([
  'activePage',
  'updatedAt',
  'cloudVersion',
  'identityRepairLocalOrigin',
  'schemaVersion',
  'projectId',
  'ownerSub',
  'createdAt',
  'sourceTemplateDomain',
  'sourceClientProfile',
  'compositionContext',
  'brandKit'
]);

/** Shape of a stored/transmitted content hash: 64 lowercase hex chars. */
export const CONTENT_HASH_RE = /^[0-9a-f]{64}$/;

/**
 * @param {object} preparedState — the PORTABLE state (what becomes state.json:
 *   already cloned, cloudVersion-stripped, media canonicalized). Hashing live
 *   state instead silently produces false "no change" verdicts — the live
 *   object differs from what was actually saved.
 * @returns {Promise<string>} 64-hex SHA-256
 */
export async function versionContentHashOf(preparedState) {
  const clone = structuredClone(preparedState);
  for (const path of VERSION_HASH_IGNORED_PATHS) {
    if (clone != null && typeof clone === 'object') delete clone[path];
  }
  if (clone && typeof clone === 'object') {
    const acceptKeys = acceptKeysForIdentity(clone.auditAccepts);
    if (acceptKeys.length > 0) clone.auditAccepts = acceptKeys;
    else delete clone.auditAccepts;
  }
  const text = canonicalStringify(clone);
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
